import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import {
	MatTableDataSource,
	MatTable,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { PhotoGalleryComponent } from 'src/app/components/photo-gallery/photo-gallery.component';
import { DeviceNote, DeviceNotePhoto } from 'src/app/models/data/device';
import { User } from 'src/app/models/data/user';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import { ShortNamedOffsetPipe } from '../../../components/pipes';

@Component({
	selector: 'app-device-notes-home',
	templateUrl: './device-notes-home.component.html',
	styleUrls: ['./device-notes-home.component.scss'],
	imports: [
		DataSetComponent_1,
		DataSetContentComponent,
		MatProgressSpinner,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatIcon,
		MatTooltip,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatPaginator,
		DatePipe,
		ShortNamedOffsetPipe,
	],
})
export class DeviceNotesHomeComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild('paginator') paginator!: MatPaginator;

	dataSource = new MatTableDataSource<{
		deviceNote: DeviceNote;
		user: User;
		deviceNotePhotos: DeviceNotePhoto[];
	}>();

	displayedColumns: string[] = ['photos', 'note', 'deviceId', 'rowMenu'];

	notePhotosTooltip = $localize`:@@deviceNotesHome-notePhotosTooltip:Note Photos`;

	pageSizeOptions = [5, 10];
	currentPage = 0;
	totalRows = 0;
	pageSize = 5;
	noteTotals?: number;

	private getAllDeviceNotesSubscription?: Subscription;

	getAllDeviceNotesLoadingKey = ApiLoadingKeys.getAllDeviceNotes;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly dialog: MatDialog,
		private router: Router,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.getTableData();
	}

	ngOnDestroy(): void {
		this.getAllDeviceNotesSubscription?.unsubscribe();
		this.getAllDeviceNotesSubscription = undefined;
	}

	getTableData(): void {
		const queryParameters: QueryParam[] = [
			{ param: 'rowIndex', value: this.currentPage * this.pageSize },
			{ param: 'rowCount', value: this.pageSize },
			{ param: 'sortBy', value: 'dateAdded' },
			{ param: 'sortDirection', value: 'desc' },
		];

		this.setData(queryParameters);
	}

	async setData(queryParameters: QueryParam[]): Promise<void> {
		this.apiService.getAllDeviceNotes(queryParameters).then((result) => {
			this.dataSource.data = result.data;
			this.dataSource.data.length = result.data.length;
			this.noteTotals = result.dataTotals;
			this.finishedLoadingInitData = true;
		});
	}

	showPhotos(event: MouseEvent, photos: DeviceNotePhoto[]): void {
		// this allows user to click on photo and not navigate them to device/notes page
		event.stopPropagation();
		const photoGalleryItems = photos.map((it) => ({
			photoUrl: it.photoUrl,
			caption: it.caption,
		}));
		this.dialog.open(PhotoGalleryComponent, { data: photoGalleryItems });
	}

	onRowClick(element: any): void {
		this.router.navigateByUrl(
			`/devices/${element.deviceNote.deviceId}?tabIndex=2`
		);
	}

	handlePageEvent(event: PageEvent): void {
		this.currentPage = event.pageIndex;
		this.pageSize = event.pageSize;
		this.getTableData();
	}

	async dismissNote(event: MouseEvent, deviceNote: DeviceNote): Promise<void> {
		// this allows user to click on X and not navigate them to device/notes page
		event.stopPropagation();

		await this.apiService.dismissNote(deviceNote).then((res) => {
			if (res) {
				this.currentPage = 0; // reset this before reloading the notes from server
				this.getTableData();
			}
		});
	}

	formatNoteTypeDisplayName(originalNoteType: string) {
		switch (originalNoteType) {
			case 'ManualEnd':
				return 'Manual End';
			case 'AppVisit':
				return 'Visit';
			default:
				return originalNoteType;
		}
	}

	rowId(row: {
		deviceNote: DeviceNote;
		user: User;
		deviceNotePhotos: DeviceNotePhoto[];
	}): string {
		if (!row?.deviceNote) {
			return 'unknown-id';
		}
		return `${row.deviceNote.deviceId}-${row.deviceNote.utcEntryTime}-${row.deviceNote.userId}`;
	}
}
